import { ObjectType, SingleStepSportEvent, SportType } from '@/@types/graphql-types';
import type { WebLocale } from '@/enums/web-locale';
import { SportPageType } from '@/features/sports/types/interfaces';

// using import type to fix "export not found" errors with TS
import type {
	OrderedCompetitors,
	SingleStepCompetitionContext,
	SingleStepContext,
	SingleStepEventContext,
	SingleStepOverviewContext,
	SingleStepTeamContext,
} from '@/features/sports/helpers/config/singleStepContexts';

import americanFootball from './american-football';
import baseball from './baseball';
import basketball from './basketball';
import cricket from './cricket';
import football from './football';
import iceHockey from './ice-hockey';
import rugby from './rugby';
import tennis from './tennis';

/** Get the context needed for the Singlestep Overview page. */
function getContext({ sport, pageType }: SportContextOptions<ObjectType.Sport>): SingleStepOverviewContext;
/** Get the context needed for the Singlestep Competition page. */
function getContext({
	sport,
	pageType,
}: SportContextOptions<ObjectType.SportCompetition>): SingleStepCompetitionContext;
/** Get the context needed for the Singlestep Event page. */
function getContext({ sport, pageType }: SportContextOptions<ObjectType.SportEvent>): SingleStepEventContext;
function getContext({ sport, pageType }: SportContextOptions<ObjectType.SportCompetitor>): SingleStepTeamContext;
function getContext<T extends ActiveSportPageType>({ sport, pageType }: SportContextOptions<T>): SingleStepContext {
	switch (sport) {
		case SportType.AmericanFootball:
			return americanFootball[pageType]();
		case SportType.Baseball:
			return baseball[pageType]();
		case SportType.Basketball:
			return basketball[pageType]();
		case SportType.Football:
			return football[pageType]();
		case SportType.IceHockey:
			return iceHockey[pageType]();
		case SportType.Rugby:
			return rugby[pageType]();
		case SportType.Tennis:
			return tennis[pageType]();
		case SportType.Cricket:
			return cricket[pageType]();
		default:
			throw new Error(`[Sports Config] Unknown sport type: ${sport}`);
	}
}

export { getContext as getSportPageContext };

//////////////////////////
//* EVENT EDGE CONTEXT *//
//////////////////////////

export interface EventsEdgeContext {
	/**
	 * Translation key for the sport's name.
	 *
	 * @example
	 * ```html
	 * <p>{{ $t(context.sportName) }}</p>
	 * ```
	 */
	sportName: string;

	/**
	 * Translation key for the kind of sport event (Match or Race).
	 *
	 * @example
	 * ```html
	 * <p>{{ $t(context.eventType) }}</p>
	 * ```
	 */
	eventType: string;

	/** Tracking page type. */
	pageType: typeof SportPageType.AllSports;

	/** The technical sport name. */
	sportType: SportType;

	getCompetitors: (event: SingleStepSportEvent, webLocale: WebLocale) => OrderedCompetitors;
}

/** Create the pieces of context needed in the All Sports page. */
function eventsEdgeContext(sportType: SportType): EventsEdgeContext {
	// Hijacking a random page type context to get per-sport properties
	const { sportName, eventType, getCompetitors } =
		getContext({
			sport: sportType,
			pageType: SportPageType.SportOverview,
		}) ?? {};

	return {
		/** TEXT */
		sportName,
		eventType,
		getCompetitors,

		/** TRACKING */
		pageType: SportPageType.AllSports,

		/** MISC */
		sportType,
	};
}

/////////////////
//* UTILITIES *//
/////////////////

type ActiveSportPageType = Exclude<SportPageType, 'ALL_SPORTS' | 'SPORT_HOME' | 'GENERIC'>;
interface SportContextOptions<T extends ActiveSportPageType> {
	sport: SportType;
	pageType: T;
}

/** Type assertion utility for sport contexts. */
const contextIs = {
	/** Assert the sport context is an EventsEdgeContext */
	allSports: (context: any): context is EventsEdgeContext => context.pageType === SportPageType.AllSports,

	/** Assert the sport context is a SingleStepOverviewContext */
	overview: (context: any): context is SingleStepOverviewContext => context.pageType === SportPageType.SportOverview,

	/** Assert the sport context is a SingleStepCompetitionContext */
	competition: (context: any): context is SingleStepCompetitionContext =>
		context.pageType === SportPageType.SportCompetition,

	/** Assert the sport context is a SingleStepEventContext */
	event: (context: any): context is SingleStepEventContext => context.pageType === SportPageType.SportEvent,

	/** Assert the sport context is a SingleStepTeamContext */
	team: (context: any): context is SingleStepTeamContext => context.pageType === SportPageType.SportTeam,
} as const;

export interface SportConfigExport {
	[ObjectType.Sport]: () => SingleStepOverviewContext;
	[ObjectType.SportCompetition]: () => SingleStepCompetitionContext;
	[ObjectType.SportEvent]: () => SingleStepEventContext;
	[ObjectType.SportCompetitor]: () => SingleStepTeamContext;
}

export { contextIs, eventsEdgeContext };

export type {
	SingleStepCompetitionContext,
	SingleStepContext,
	SingleStepEventContext,
	SingleStepOverviewContext,
	SingleStepTeamContext,
};
